import { ErrorBoundary } from 'react-error-boundary';
import { ReactElement } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { DiLogo } from '../di-logo';
import { ErrorCodeBlock } from './error-code-block';

interface ErrorFallbackProp {
    error: Error;
}

export function ErrorFallback({ error }: ErrorFallbackProp): ReactElement {
    const sessionId = datadogRum.getInternalContext()?.session_id;

    // eslint-disable-next-line no-console
    console.error({
        sessionId,
        error,
    });

    const styles = {
        font: {
            fontFamily: 'Roboto',
        },
        button: {
            backgroundColor: '#FF6400',
            border: 'none',
            borderRadius: '5px',
            color: 'white',
            padding: '10px 20px',
            cursor: 'pointer',
            marginTop: '20px',
        },
    };

    return (
        <div
            role="alert"
            style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
            }}
        >
            <DiLogo variant="horizontalRGB" w="80vw" maxW="500px" mb="30px" />
            {
                import.meta.env.VITE_PUBLIC_ENVIRONMENT !== 'production' ? (
                    // Only display the error message in development
                    <div>
                        <p style={styles.font}>
                            The following error was encountered:
                        </p>
                        <ErrorCodeBlock
                            error={error}
                            sessionId={sessionId}
                        />
                    </div>
                ) : (
                    <p style={styles.font}>
                        Uh oh, something went wrong.
                    </p>
                )
            }
            <button type="button" onClick={() => window.location.reload()} style={styles.button}>
                <strong>Reload this page</strong>
            </button>
        </div>
    );
}

export function RootErrorBoundary({ children }: React.PropsWithChildren) {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            {children}
        </ErrorBoundary>
    );
}
